import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getWins, createWin, deleteWin, generateBragReel, generateBio, getProfile, getAllSessions, getSuggestedWins,
  getMarketingPage, generateMarketingPage, updateMarketingPage, uploadMarketingPhoto, deleteMarketingPhoto, PORTAL_BASE_URL
} from '../api/client';
import MarketingPageView from '../components/MarketingPageView';
import LoadingLeaf from '../components/LoadingLeaf';
import { exportMarketingPageHTML } from '../utils/htmlExport';

export default function Marketing() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('wins');
  const [wins, setWins] = useState([]);
  const [suggestedWins, setSuggestedWins] = useState([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [newWinText, setNewWinText] = useState('');
  const [addingWin, setAddingWin] = useState(false);
  const [bragReel, setBragReel] = useState(null);
  const [generatingBrag, setGeneratingBrag] = useState(false);
  const [bio, setBio] = useState(null);
  const [generatingBio, setGeneratingBio] = useState(false);
  const [profile, setProfile] = useState(null);
  const [bioForm, setBioForm] = useState({
    years_experience: '',
    client_types: '',
    biggest_outcome: '',
    philosophy: '',
  });
  const [error, setError] = useState('');

  const [marketingPage, setMarketingPage] = useState(null);
  const [generatingPage, setGeneratingPage] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [touchedFields, setTouchedFields] = useState(new Set());
  const [pageForm, setPageForm] = useState({
    client_types: '',
    biggest_pain_point: '',
    biggest_outcome: '',
    philosophy: '',
    cta_text: '',
    cta_url: '',
  });
  const [slugInput, setSlugInput] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pageMsg, setPageMsg] = useState('');

  // Autosave (replaces the old explicit "Save changes" button): debounce on
  // every keystroke, flush immediately on blur or on a discrete action like
  // add/remove outcome. marketingPageRef mirrors marketingPage so flushSave
  // can read the latest value without needing to be re-created on every
  // change (its own identity must stay stable for the debounce timer to work
  // correctly across renders).
  const marketingPageRef = useRef(marketingPage);
  useEffect(() => { marketingPageRef.current = marketingPage; }, [marketingPage]);
  const autosaveTimer = useRef(null);

  const flushSave = useCallback(async (pageOverride) => {
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    const current = pageOverride || marketingPageRef.current;
    if (!current) return;
    setSavingPage(true);
    try {
      const { headline, hook_subtext, pain_point, transformation, approach, outcomes, cta_text, cta_url } = current;
      await updateMarketingPage({ headline, hook_subtext, pain_point, transformation, approach, outcomes, cta_text, cta_url });
      // Deliberately NOT reconciling local state with the response here (unlike
      // the old handleSavePage) — under debounced autosave that reconciliation
      // is exactly what could clobber newer local edits with a stale response
      // if two saves overlap. The PUT payload already IS the full local state
      // for every field it touches, so there's nothing to reconcile.
      setPageMsg('Saved');
    } catch {
      setPageMsg('Could not save changes.');
    } finally {
      setSavingPage(false);
    }
  }, []);

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(flushSave, 1200);
  }, [flushSave]);

  const handleFieldBlur = useCallback(() => { flushSave(); }, [flushSave]);

  // Flush on tab-hide so a coach who edits then immediately closes the tab
  // within the debounce window doesn't silently lose that last edit.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && autosaveTimer.current) flushSave();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [flushSave]);

  useEffect(() => {
    if (user) {
      fetchWins();
      fetchProfile();
      fetchMarketingPage();
    }
  }, [user]);

  const fetchMarketingPage = async () => {
    try {
      const res = await getMarketingPage();
      if (res.data) {
        setMarketingPage(res.data);
        setSlugInput(res.data.slug || '');
      }
    } catch {}
  };

  const fetchWins = async () => {
    try {
      const [winsRes, suggestedRes] = await Promise.all([getWins(), getSuggestedWins()]);
      setWins(winsRes.data || []);
      setSuggestedWins(suggestedRes.data || []);
    } catch {
      setError('Could not load wins.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuggested = async (suggestion) => {
    try {
      const res = await createWin({ text: suggestion.quote, session_date: suggestion.session_date });
      setWins(prev => [res.data, ...prev]);
      setDismissedSuggestions(prev => new Set([...prev, suggestion.quote]));
    } catch {
      setError('Could not save win.');
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await getProfile();
      setProfile(res.data);
    } catch {}
  };

  const handleAddWin = async () => {
    if (!newWinText.trim() || addingWin) return;
    setAddingWin(true);
    try {
      const res = await createWin({ text: newWinText.trim() });
      setWins(prev => [res.data, ...prev]);
      setNewWinText('');
    } catch {
      setError('Could not save win.');
    } finally {
      setAddingWin(false);
    }
  };

  const handleDeleteWin = async (id) => {
    try {
      await deleteWin(id);
      setWins(prev => prev.filter(w => w.id !== id));
    } catch {
      setError('Could not delete win.');
    }
  };

  const handleGenerateBragReel = async () => {
    setGeneratingBrag(true);
    setBragReel(null);
    try {
      let quotes = [];
      try {
        const sessionsRes = await getAllSessions();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentSessions = (sessionsRes.data || []).filter(s => new Date(s.date) >= thirtyDaysAgo);
        recentSessions.forEach(s => {
          (s.breakthrough_quotes || []).forEach(bq => {
            if (bq.quote && quotes.length < 10) quotes.push(bq.quote);
          });
        });
      } catch {}

      const res = await generateBragReel({
        wins: wins.map(w => w.text),
        breakthrough_quotes: quotes,
      });
      setBragReel(res.data || res);
    } catch {
      setError('Could not generate content. Try again.');
    } finally {
      setGeneratingBrag(false);
    }
  };

  const handleGenerateBio = async () => {
    setGeneratingBio(true);
    setBio(null);
    try {
      const modalities = profile?.modality ? [profile.modality] : [];
      const res = await generateBio({
        modalities,
        years_experience: bioForm.years_experience ? parseInt(bioForm.years_experience, 10) : null,
        client_types: bioForm.client_types || null,
        biggest_outcome: bioForm.biggest_outcome || null,
        philosophy: bioForm.philosophy || null,
      });
      setBio(res.data || res);
    } catch {
      setError('Could not generate bio. Try again.');
    } finally {
      setGeneratingBio(false);
    }
  };

  const handleGeneratePage = async () => {
    // Cancel (not flush) any pending autosave from the pre-regenerate draft —
    // otherwise a stale queued save could fire after regenerate completes and
    // overwrite the fresh AI draft with the old content.
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    setGeneratingPage(true);
    setPageMsg('');
    try {
      const res = await generateMarketingPage(pageForm);
      setMarketingPage(res.data);
      setSlugInput(res.data.slug || '');
      setTouchedFields(new Set());
    } catch {
      setError('Could not generate your page. Try again.');
    } finally {
      setGeneratingPage(false);
    }
  };

  const handleSectionChange = (field, value) => {
    setMarketingPage(prev => ({ ...prev, [field]: value }));
    setTouchedFields(prev => new Set([...prev, field]));
    scheduleAutosave();
  };

  const handleOutcomeTextChange = (index, value) => {
    setMarketingPage(prev => ({
      ...prev,
      outcomes: prev.outcomes.map((o, i) => (i === index ? { ...o, text: value } : o)),
    }));
    scheduleAutosave();
  };

  // Add/remove are discrete one-shot clicks, not a keystroke stream, so they
  // flush immediately rather than debouncing. setMarketingPage's updater
  // computes the next value and passes it straight to flushSave, since
  // marketingPageRef won't reflect this change until after the next render —
  // reading it synchronously here would save stale (pre-change) outcomes.
  const handleAddOutcome = (text, source, source_id) => {
    setMarketingPage(prev => {
      const next = { ...prev, outcomes: [...(prev?.outcomes || []), { text, source, source_id }] };
      flushSave(next);
      return next;
    });
  };

  const handleRemoveOutcome = (index) => {
    setMarketingPage(prev => {
      const next = { ...prev, outcomes: prev.outcomes.filter((_, i) => i !== index) };
      flushSave(next);
      return next;
    });
  };

  const handlePhotoUpload = async (e, placement) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadMarketingPhoto(formData);
      const photos = res.data;
      // The upload endpoint always tags new photos as "gallery" — if this
      // upload slot is meant to be the hero photo, relabel just the photo we
      // uploaded (the last one in the returned array) and persist that.
      if (placement === 'hero' && photos.length > 0) {
        photos[photos.length - 1].placement = 'hero';
        await updateMarketingPage({ photos });
      }
      setMarketingPage(prev => ({ ...prev, photos }));
    } catch {
      setError('Could not upload photo.');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handlePhotoDelete = async (storagePath) => {
    try {
      const res = await deleteMarketingPhoto(storagePath);
      setMarketingPage(prev => ({ ...prev, photos: res.data }));
    } catch {
      setError('Could not delete photo.');
    }
  };

  const handleSaveSlug = async () => {
    setSavingSlug(true);
    setPageMsg('');
    try {
      const res = await updateMarketingPage({ slug: slugInput.trim().toLowerCase() });
      setMarketingPage(res.data);
      setPageMsg('Web address saved.');
    } catch (err) {
      setPageMsg(err.response?.data?.detail || 'Could not save that web address.');
    } finally {
      setSavingSlug(false);
    }
  };

  const handleTogglePublish = async () => {
    setPublishing(true);
    try {
      const res = await updateMarketingPage({ is_published: !marketingPage.is_published });
      setMarketingPage(res.data);
    } catch {
      setError('Could not update publish status.');
    } finally {
      setPublishing(false);
    }
  };

  const handleToggleShowOfferings = async () => {
    try {
      const res = await updateMarketingPage({ show_offerings: marketingPage.show_offerings === false });
      setMarketingPage(res.data);
    } catch {
      setError('Could not update Services section visibility.');
    }
  };

  const CopyBox = ({ label, text }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    return (
      <div style={styles.copyBox}>
        <div style={styles.copyBoxHeader}>
          <span style={styles.copyBoxLabel}>{label}</span>
          <button style={styles.copyBtn} onClick={copy}>{copied ? 'Copied!' : 'Copy'}</button>
        </div>
        <div style={styles.copyBoxText}>{text}</div>
      </div>
    );
  };

  const tabs = [
    { id: 'wins', label: 'Wins library' },
    { id: 'brag', label: 'Brag reel' },
    { id: 'bio', label: 'Bio generator' },
    { id: 'landing', label: 'Landing page' },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.pageTitle}>Marketing</div>
        <div style={styles.pageSubtitle}>Capture client wins and turn them into content for your practice</div>
      </div>

      {error && (
        <div style={styles.errorAlert}>
          {error}
          <button onClick={() => setError('')} style={styles.errorClose}>×</button>
        </div>
      )}

      <div style={styles.tabBar}>
        {tabs.map(t => (
          <button
            key={t.id}
            style={{ ...styles.tab, ...(activeTab === t.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Wins Library ── */}
      {activeTab === 'wins' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>Wins library</span>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.addRow}>
              <input
                style={{ ...styles.input, flex: 1 }}
                placeholder="Note a client transformation moment..."
                value={newWinText}
                onChange={e => setNewWinText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddWin()}
              />
              <button style={styles.addBtn} onClick={handleAddWin} disabled={addingWin}>
                {addingWin ? 'Saving...' : 'Add win'}
              </button>
            </div>

            {/* Suggested from sessions */}
            {!loading && suggestedWins.filter(s => !dismissedSuggestions.has(s.quote)).length > 0 && (
              <div style={styles.suggestionsBox}>
                <div style={styles.suggestionsLabel}>Starred breakthroughs from your sessions</div>
                <div style={styles.suggestionsHint}>These are quotes your clients said that you starred. Add them to your wins library with one click.</div>
                {suggestedWins.filter(s => !dismissedSuggestions.has(s.quote)).map((s, i) => (
                  <div key={i} style={styles.suggestionRow}>
                    <div style={styles.suggestionQuote}>"{s.quote}"</div>
                    {s.session_date && <div style={styles.suggestionDate}>{new Date(s.session_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
                    <div style={styles.suggestionActions}>
                      <button style={styles.addSuggBtn} onClick={() => handleAddSuggested(s)}>+ Add to wins</button>
                      <button style={styles.dismissSuggBtn} onClick={() => setDismissedSuggestions(prev => new Set([...prev, s.quote]))}>Dismiss</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {loading ? (
              <div style={styles.empty}><LoadingLeaf size={44} /></div>
            ) : wins.length === 0 ? (
              <div style={styles.emptyCard}>
                <div style={styles.emptyTitle}>No wins yet</div>
                <div style={styles.emptyText}>After each session, take a moment to note what shifted for your client. These moments become the foundation of your marketing.</div>
              </div>
            ) : (
              <div style={{ marginTop: '20px' }}>
                {wins.map(w => (
                  <div key={w.id} style={styles.winCard}>
                    <div style={styles.winText}>{w.text}</div>
                    <div style={styles.winMeta}>
                      {w.session_date && <span>{new Date(w.session_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                      <button style={styles.deleteBtn} onClick={() => handleDeleteWin(w.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Brag Reel ── */}
      {activeTab === 'brag' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>Brag reel export</span>
          </div>
          <div style={styles.cardBody}>
            <p style={styles.sectionDesc}>
              Generate social media content from your saved wins and recent breakthrough quotes. Uses all saved wins + breakthrough quotes from the last 30 days.
            </p>
            {wins.length === 0 && (
              <div style={styles.hintBox}>
                You have no wins saved yet. Add some from the Wins Library tab first.
              </div>
            )}
            <button
              style={{ ...styles.generateBtn, opacity: wins.length === 0 ? 0.5 : 1 }}
              onClick={handleGenerateBragReel}
              disabled={generatingBrag || wins.length === 0}
            >
              {generatingBrag ? 'Generating social posts...' : '✦ Generate social posts'}
            </button>

            {bragReel && (
              <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {bragReel.linkedin && <CopyBox label="LinkedIn post" text={bragReel.linkedin} />}
                {bragReel.instagram && <CopyBox label="Instagram caption" text={bragReel.instagram} />}
                {bragReel.testimonial_blurb && <CopyBox label="Website testimonial blurb" text={bragReel.testimonial_blurb} />}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Bio Generator ── */}
      {activeTab === 'bio' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>Bio generator</span>
          </div>
          <div style={styles.cardBody}>
            <p style={styles.sectionDesc}>
              Generate a professional coaching bio in short and long form.
            </p>

            {profile?.modality && (
              <div style={styles.modalityTag}>
                Modality: <strong>{profile.modality}</strong>
              </div>
            )}

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Years of experience</label>
                <input
                  style={styles.input}
                  type="number"
                  placeholder="5"
                  value={bioForm.years_experience}
                  onChange={e => setBioForm(f => ({ ...f, years_experience: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Types of clients you work with</label>
                <input
                  style={styles.input}
                  placeholder="e.g. mid-career professionals, entrepreneurs..."
                  value={bioForm.client_types}
                  onChange={e => setBioForm(f => ({ ...f, client_types: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Your biggest client outcome</label>
                <input
                  style={styles.input}
                  placeholder="e.g. clients leave corporate jobs to build fulfilling practices..."
                  value={bioForm.biggest_outcome}
                  onChange={e => setBioForm(f => ({ ...f, biggest_outcome: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Coaching philosophy (one sentence)</label>
                <input
                  style={styles.input}
                  placeholder="e.g. real change comes from within, not from a framework..."
                  value={bioForm.philosophy}
                  onChange={e => setBioForm(f => ({ ...f, philosophy: e.target.value }))}
                />
              </div>
            </div>

            <button style={styles.generateBtn} onClick={handleGenerateBio} disabled={generatingBio}>
              {generatingBio ? 'Writing your bio...' : '✦ Generate my bio'}
            </button>

            {bio && (
              <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {bio.short_bio && <CopyBox label="Short bio (50–75 words)" text={bio.short_bio} />}
                {bio.full_bio && <CopyBox label="Full bio (150–200 words)" text={bio.full_bio} />}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Landing Page Builder ── */}
      {activeTab === 'landing' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>Landing page builder</span>
          </div>
          <div style={styles.cardBody}>
            <p style={styles.sectionDesc}>
              We'll draft a starting point using the A.U.T.H.O.R. framework — Attention, Uncover,
              Transformation, How, Outcome, Response. Nothing here is meant to be published as-is —
              edit every section until it sounds like you before you publish.
            </p>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Who you work with</label>
                <input style={styles.input} placeholder="e.g. mid-career professionals feeling stuck..." value={pageForm.client_types} onChange={e => setPageForm(f => ({ ...f, client_types: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>The pain point they start with</label>
                <input style={styles.input} placeholder="e.g. burned out, unsure what's next..." value={pageForm.biggest_pain_point} onChange={e => setPageForm(f => ({ ...f, biggest_pain_point: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Your biggest client outcome</label>
                <input style={styles.input} placeholder="e.g. clients leave with real clarity and momentum..." value={pageForm.biggest_outcome} onChange={e => setPageForm(f => ({ ...f, biggest_outcome: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Your philosophy/approach</label>
                <input style={styles.input} placeholder="e.g. real change comes from within, not from a framework..." value={pageForm.philosophy} onChange={e => setPageForm(f => ({ ...f, philosophy: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Call-to-action text</label>
                <input style={styles.input} placeholder="e.g. Book a discovery call" value={pageForm.cta_text} onChange={e => setPageForm(f => ({ ...f, cta_text: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Where the button links to</label>
                <input style={styles.input} placeholder="e.g. https://calendly.com/you" value={pageForm.cta_url} onChange={e => setPageForm(f => ({ ...f, cta_url: e.target.value }))} />
              </div>
            </div>

            <button style={styles.generateBtn} onClick={handleGeneratePage} disabled={generatingPage}>
              {generatingPage ? 'Drafting your page...' : marketingPage ? '✦ Regenerate draft' : '✦ Draft my page'}
            </button>

            {(savingPage || pageMsg) && <div style={styles.pageMsg}>{savingPage ? 'Saving…' : pageMsg}</div>}

            {marketingPage && (
              <div style={{ marginTop: '28px' }}>
                <div style={styles.sectionCard}>
                  <div style={styles.sectionCardHeader}>
                    <span style={styles.sectionCardLabel}>Your page's web address</span>
                  </div>
                  <div style={styles.addRow}>
                    <span style={styles.slugPrefix}>{PORTAL_BASE_URL.replace('https://', '')}/site/</span>
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      value={slugInput}
                      onChange={e => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="your-name"
                    />
                    <button style={styles.addBtn} onClick={handleSaveSlug} disabled={savingSlug || !slugInput.trim()}>
                      {savingSlug ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {marketingPage.slug && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
                      <button
                        style={{ ...styles.addBtn, background: marketingPage.is_published ? 'var(--red)' : 'var(--sage)' }}
                        onClick={handleTogglePublish}
                        disabled={publishing}
                      >
                        {publishing ? 'Updating...' : marketingPage.is_published ? 'Unpublish' : 'Publish'}
                      </button>
                      {marketingPage.is_published && (
                        <a href={`${PORTAL_BASE_URL}/site/${marketingPage.slug}`} target="_blank" rel="noreferrer" style={styles.liveLink}>
                          View live page →
                        </a>
                      )}
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', fontSize: '12.5px', color: 'var(--text-soft)' }}>
                    <input type="checkbox" checked={marketingPage.show_offerings !== false} onChange={handleToggleShowOfferings} />
                    Show a "What I offer" section from my Offerings
                  </label>
                </div>

                <p style={{ ...styles.sectionDesc, marginTop: '20px', marginBottom: '8px' }}>
                  Click directly on the page below to edit it — headline, text, photos, and outcomes all edit in place. Changes save automatically.
                </p>
                <div style={{ ...styles.sectionCard, padding: 0, overflow: 'hidden' }}>
                  <MarketingPageView
                    page={marketingPage}
                    coachName={profile?.practice_name || profile?.full_name}
                    editable
                    touchedFields={touchedFields}
                    onFieldChange={handleSectionChange}
                    onFieldBlur={handleFieldBlur}
                    onOutcomeChange={handleOutcomeTextChange}
                    onOutcomeRemove={handleRemoveOutcome}
                    onAddOutcome={handleAddOutcome}
                    wins={wins}
                    suggestedWins={suggestedWins}
                    onPhotoUpload={handlePhotoUpload}
                    onPhotoDelete={handlePhotoDelete}
                    uploadingPhoto={uploadingPhoto}
                  />
                </div>

                <button
                  style={{ ...styles.generateBtn, marginTop: '16px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)' }}
                  onClick={() => exportMarketingPageHTML(marketingPage, profile)}
                >
                  ⬇ Download as HTML
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', maxWidth: '820px', fontFamily: "'DM Sans', sans-serif" },
  header: { marginBottom: '28px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  errorClose: { background: 'none', border: 'none', color: 'var(--red)', fontSize: '18px', cursor: 'pointer' },
  tabBar: { display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--mint)', borderRadius: '10px', padding: '4px', width: 'fit-content' },
  tab: { padding: '8px 18px', borderRadius: '7px', border: 'none', background: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', color: 'var(--text-soft)', cursor: 'pointer' },
  tabActive: { background: 'white', color: 'var(--text-dark)', fontWeight: '500', boxShadow: 'var(--shadow-sm)' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '16px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '12px' },
  cardTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' },
  cardBody: { padding: '24px' },
  addRow: { display: 'flex', gap: '10px', marginBottom: '8px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
  addBtn: { padding: '10px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: '500' },
  empty: { textAlign: 'center', padding: '40px', color: 'var(--text-soft)', fontSize: '14px' },
  emptyCard: { background: 'var(--cream)', borderRadius: '10px', padding: '24px', textAlign: 'center', marginTop: '20px' },
  emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '17px', color: 'var(--text-dark)', marginBottom: '8px' },
  emptyText: { fontSize: '13.5px', color: 'var(--text-soft)', lineHeight: 1.6 },
  winCard: { background: 'var(--cream)', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' },
  winText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.6, marginBottom: '8px' },
  winMeta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-soft)' },
  deleteBtn: { background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  sectionDesc: { fontSize: '13.5px', color: 'var(--text-soft)', lineHeight: 1.6, marginBottom: '20px' },
  hintBox: { background: 'var(--gold-pale)', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' },
  generateBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--gold)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  copyBox: { background: 'var(--cream)', borderRadius: '10px', padding: '16px', border: '1px solid var(--border-light)' },
  copyBoxHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
  copyBoxLabel: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)' },
  copyBtn: { padding: '4px 12px', borderRadius: '6px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  copyBoxText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7, whiteSpace: 'pre-wrap' },
  modalityTag: { background: 'var(--mint)', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: 'var(--text-mid)', marginBottom: '20px', display: 'inline-block' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' },
  formGroup: {},
  label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '6px' },
  suggestionsBox: { background: 'var(--gold-pale)', border: '1px solid rgba(196,149,106,0.25)', borderRadius: '10px', padding: '16px', marginBottom: '20px' },
  suggestionsLabel: { fontSize: '11.5px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gold)', marginBottom: '4px' },
  suggestionsHint: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '14px', lineHeight: 1.5 },
  suggestionRow: { background: 'white', borderRadius: '8px', padding: '12px 14px', marginBottom: '10px', borderLeft: '3px solid var(--gold)' },
  suggestionQuote: { fontFamily: "'Lora', Georgia, serif", fontSize: '13.5px', fontStyle: 'italic', color: 'var(--text-dark)', lineHeight: 1.6, marginBottom: '4px' },
  suggestionDate: { fontSize: '11.5px', color: 'var(--text-faint)', marginBottom: '8px' },
  suggestionActions: { display: 'flex', gap: '8px' },
  addSuggBtn: { padding: '5px 12px', borderRadius: '6px', background: 'var(--gold)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  dismissSuggBtn: { padding: '5px 10px', borderRadius: '6px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-faint)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' },

  // Landing page builder
  pageMsg: { fontSize: '13px', color: 'var(--sage)', marginTop: '10px' },
  sectionCard: { background: 'var(--cream)', borderRadius: '10px', padding: '16px', marginTop: '14px', border: '1px solid var(--border-light)' },
  sectionCardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' },
  sectionCardLabel: { fontSize: '11.5px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)' },
  slugPrefix: { fontSize: '13px', color: 'var(--text-faint)', whiteSpace: 'nowrap', alignSelf: 'center' },
  liveLink: { fontSize: '13px', color: 'var(--sage)', fontWeight: '500', textDecoration: 'none' },
};
